// Rename to step-6-1-rfp-subcriteria-evaluation / Step61RfpSubCriteriaEvaluationComponent similar to step-5-1-rfi-xxx
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import {rxResource} from '@angular/core/rxjs-interop';
import {firstValueFrom, forkJoin, from, map, Observable, switchMap} from 'rxjs';
import {Button} from 'primeng/button';
import {Card} from 'primeng/card';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Tooltip} from 'primeng/tooltip';
import {TranslocoPipe} from '@jsverse/transloco';
import {
  DirectusService,
  DirectusTranslation,
  EvaluationCriteria,
  SubcriteriaAttributeDirectus,
} from '@frontend/directus';
import {TranslateDirectusPipe} from '@frontend/transloco';
import {
  AttributeConsistencyStatus,
  CompleteRfpEvaluationSubcriteriaStepRequestParams,
  EvaluationCriteriaControllerService,
  EvaluationCriteriaResponse,
  EvaluationSubCriteriaAttributeView,
  EvaluationSubCriteriaImportanceScore,
  EvaluationSubCriteriaView,
  ImportanceScoreView,
  ParticipatingToolControllerService,
  ProjectRoleType,
  ProjectStepType,
  TenderPhaseType,
} from '@frontend/shared-api';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {ProgressDashboardComponent} from '../../../../../../shared/components/progress-dashboard/progress-dashboard.component';
import {ContributorMenuBarComponent} from '../../../../../../shared/components/contributor-menu-bar/contributor-menu-bar.component';
import {ProjectUserDetailService} from '../../../../../../shared/services/project-user-detail.service';
import {RoleCheckService} from '../../../../../../shared/services/role-check.service';
import {ImportanceScoreProgressService} from '../../../../../../shared/services/importance-score-progress.service';
import {FixedValueService} from '../../../../../../shared/services/fixed-value.service';
import {Ec3ModuleGroupingService} from './ec3-module-group/ec3-module-grouping.service';
import {ModuleTab} from './ec3-module-group/ec3-module-group.types';
import {
  AttributesByEvaluationCriteriaMap,
  AttributesBySubCriteriaMap,
} from '../../../../../../shared/components/subcriteria-attribute-evaluation/subcriteria-attribute-evaluation.types';
import {TabStatusEnum} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {logTabRender} from '../../../../../../shared/tabs/tab-render-log.util';
import {
  AVERAGE_USER_EMAIL,
  AVERAGED_SCORE_AUTHOR,
} from '../../../../../../shared/constants/contributor.constants';
import {EVALUATION_CRITERIA} from '../../../../../../shared/constants/evaluation-criteria.constants';
import {
  AttributeConsistencyChange,
  AttributeScoreVM,
} from '../../../../../../shared/components/sub-criteria-tab/sub-criteria-tab.types';
import {AttributeConsistencyService} from '../../../../../../shared/services/attribute-consistency.service';
import {ATTRIBUTE_CONSISTENCY_STATUS_COLORS} from '../../../../../../shared/constants/attribute-consistency.constants';
import {
  CriteriaCatalog,
  EMPTY_CRITERIA_CATALOG,
  EMPTY_MODULE_TABS,
  EMPTY_PROJECT_SCORES,
  EvaluationCriteriaTabVM,
  ProjectScores,
  SubCriteriaTabVM,
} from './step-6-1-evaluation-criteria-comparison.types';
import {SavedSubCriteriaScores} from './evaluation-criteria-tab/evaluation-criteria-tab.types';
import {Step61EvaluationCriteriaComparisonSummaryComponent} from './step-6-1-evaluation-criteria-comparison-summary/step-6-1-evaluation-criteria-comparison-summary.component';
import {EvaluationCriteriaTabComponent} from './evaluation-criteria-tab/evaluation-criteria-tab.component';
import {GlobalSearchComponent} from '../../../../../../shared/components/search/global-search/global-search.component';
import {aggregateSummaryTabStatus, aggregateTabStatus} from './tab-status';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';
import {ConfirmationDialogService} from '../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

const ATTRIBUTE_COLLECTION_BASE_NAME = 'subcriteria_attribute';
const COLLECTION_POSTFIX = 'rfp';
const SUMMARY_TAB_VALUE = 'summary';
const ATTRIBUTE_IDENTIFIER_PREFIX = 'SCA';

@Component({
  selector: 'app-step-6-1-evaluation-criteria-comparison',
  imports: [
    Button,
    Card,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tooltip,
    TranslocoPipe,
    TranslateDirectusPipe,
    LoadingComponent,
    ProgressDashboardComponent,
    ContributorMenuBarComponent,
    Step61EvaluationCriteriaComparisonSummaryComponent,
    EvaluationCriteriaTabComponent,
    GlobalSearchComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-6-1-evaluation-criteria-comparison.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Step61EvaluationCriteriaComparisonComponent extends ProjectStepBaseReactive {
  protected readonly contributorRole = ProjectRoleType.CONTRIBUTOR;
  protected readonly rfpEvaluationSubcriteria = ProjectStepType.RFP_EVALUATION_SUBCRITERIA;
  protected readonly summaryTabValue = SUMMARY_TAB_VALUE;

  private readonly directusService = inject(DirectusService);
  private readonly evaluationCriteriaController = inject(EvaluationCriteriaControllerService);
  private readonly participatingToolController = inject(ParticipatingToolControllerService);
  private readonly progressService = inject(ImportanceScoreProgressService);
  private readonly consistencyService = inject(AttributeConsistencyService);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly ec3GroupingService = inject(Ec3ModuleGroupingService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly projectUserDetail = inject(ProjectUserDetailService).createUserDetailSignal(
    () => this.projectId(),
    inject(DestroyRef),
  );

  protected readonly selectedContributorEmail = signal<string | undefined>(undefined);
  protected readonly searchTerm = signal('');
  private readonly selectedTabValue = signal<number | string | null>(null);
  private readonly explicitlyOpenedTabValues = signal(new Set<number | string>());
  protected readonly criteriaIdsWithUnsavedScores = signal(new Set<number>());
  protected readonly isFinishingStep = signal(false);
  private readonly initialLoadDone = signal(false);

  private readonly catalogResource = rxResource({
    params: () => this.projectId(),
    stream: () => this.loadCatalog(),
    defaultValue: EMPTY_CRITERIA_CATALOG,
  });

  private readonly scoresResource = rxResource({
    params: () => this.scoresRequest(),
    stream: ({params: request}) =>
      this.loadProjectScores(request.projectId, request.contributorEmail),
    defaultValue: EMPTY_PROJECT_SCORES,
  });

  private readonly ec3ModulesResource = rxResource({
    params: () => this.projectId(),
    stream: () => from(this.ec3GroupingService.loadAndGroupModuleTabs(EVALUATION_CRITERIA.EC3)),
    defaultValue: EMPTY_MODULE_TABS,
  });

  protected readonly isProjectManager = computed<boolean>(() => {
    return (this.projectUserDetail()?.roles ?? []).includes(ProjectRoleType.PROJECT_MANAGER);
  });

  private readonly previousPhaseResource = rxResource({
    params: () => (this.isProjectManager() ? this.projectId() : undefined),
    stream: ({params: projectId}) => this.loadPreviousPhaseFinished(projectId),
    defaultValue: false,
  });

  private readonly catalog = computed<CriteriaCatalog>(() => {
    return this.catalogResource.value();
  });

  private readonly projectScores = computed<ProjectScores>(() => {
    return this.scoresResource.value();
  });

  protected readonly previousPhaseFinished = computed<boolean>(() => {
    return this.previousPhaseResource.value();
  });

  protected readonly tabEc3ModuleTabs = computed<ModuleTab[]>(() => {
    return this.ec3ModulesResource.value();
  });

  private readonly resourcesLoading = computed<boolean>(() => {
    return (
      this.isLoadingState() ||
      this.catalogResource.isLoading() ||
      this.scoresResource.isLoading() ||
      this.ec3ModulesResource.isLoading()
    );
  });

  protected readonly loading = computed<boolean>(() => {
    return (!this.initialLoadDone() && this.resourcesLoading()) || this.scoresResource.isLoading();
  });

  protected readonly comparableCriteria = computed<EvaluationCriteriaTabVM[]>(() => {
    const catalog = this.catalog();
    const scoreAuthor = this.scoreAuthor();
    const projectScores = this.projectScores();

    return projectScores.criteria
      .filter((criteria) => this.isComparable(criteria))
      .map((criteria) =>
        this.toCriteriaTab(criteria, catalog, projectScores.attributeIdsBySubCriteria, scoreAuthor),
      )
      .sort((first, second) => first.ecNumber - second.ecNumber);
  });

  protected readonly hasCriteria = computed<boolean>(() => {
    return this.comparableCriteria().length > 0;
  });

  private readonly normalizedSearchTerm = computed<string>(() => {
    return this.searchTerm().trim().toLowerCase();
  });

  protected readonly filteredCriteria = computed<EvaluationCriteriaTabVM[]>(() => {
    const term = this.normalizedSearchTerm();
    const criteria = this.comparableCriteria();

    if (term === '') {
      return criteria;
    }

    return criteria
      .map((item) => this.narrowCriteriaToTerm(item, term))
      .filter((item) => item !== null);
  });

  protected readonly filteredCriteriaUnsaved = computed<boolean[]>(() => {
    const criteriaIdsWithUnsavedScores = this.criteriaIdsWithUnsavedScores();

    return this.filteredCriteria().map((criteria) => criteriaIdsWithUnsavedScores.has(criteria.id));
  });

  protected readonly showNoSearchResults = computed<boolean>(() => {
    return this.normalizedSearchTerm() !== '' && this.filteredCriteria().length === 0;
  });

  protected readonly activeTabValue = computed<number | string>(() => {
    const selectedTabValue = this.selectedTabValue();
    const criteria = this.filteredCriteria();

    if (selectedTabValue === SUMMARY_TAB_VALUE) {
      return selectedTabValue;
    }

    const selectedCriteria = criteria.find((item) => item.id === selectedTabValue);

    return selectedCriteria?.id ?? criteria[0]?.id ?? SUMMARY_TAB_VALUE;
  });

  protected readonly openedTabValues = computed<Set<number | string>>(() => {
    return new Set([...this.explicitlyOpenedTabValues(), this.activeTabValue()]);
  });

  protected readonly summaryTabStatus = computed<TabStatusEnum>(() => {
    return aggregateSummaryTabStatus(this.comparableCriteria().map((criteria) => criteria.status));
  });

  protected readonly dashboardDirectusCriteria = computed<EvaluationCriteria[]>(() => {
    const catalog = this.catalog();

    return this.projectScores()
      .criteria.map((criteria) => catalog.criteria.find((item) => item.id === criteria.directusId)!)
      .sort((first, second) => first.ecNumber - second.ecNumber);
  });

  protected readonly dashboardDirectusSubCriteria = computed(() => this.catalog().subCriteria);

  protected readonly dashboardAttributesByCriteria = computed<AttributesByEvaluationCriteriaMap>(
    () => {
      const attributesByCriteria: AttributesByEvaluationCriteriaMap = {};

      this.comparableCriteria().forEach((criteria) => {
        const attributesBySubCriteria: AttributesBySubCriteriaMap = {};

        criteria.subCriteria.forEach((subCriteria) => {
          attributesBySubCriteria[subCriteria.id] = subCriteria.attributes.map((attribute) => ({
            id: attribute.id,
            ecNumber: criteria.ecNumber,
            scNumber: subCriteria.scNumber,
            directusId: attribute.directusId,
            title: '',
            translations: attribute.translations,
            scaNumber: attribute.scaNumber,
            selectedImportanceScore: attribute.score,
            hasChanges: false,
            isSaving: false,
          }));
        });

        attributesByCriteria[criteria.id] = attributesBySubCriteria;
      });

      return attributesByCriteria;
    },
  );

  private readonly contributorEmail = computed<string | undefined>(
    () => this.selectedContributorEmail() ?? this.currentUserEmail(),
  );

  protected readonly consistencyWarningColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.REVIEW_REQUIRED];
  protected readonly consistencyClarificationColor =
    ATTRIBUTE_CONSISTENCY_STATUS_COLORS[AttributeConsistencyStatus.CLARIFICATION_REQUIRED];

  protected readonly canSeeAverages = computed<boolean>(
    () => this.isProjectManager() || this.roleCheckService.hasCustomerAdminRole(),
  );

  protected readonly showConsistency = computed<boolean>(
    () => this.contributorEmail() === AVERAGE_USER_EMAIL && this.canSeeAverages(),
  );

  protected readonly isDisabled = computed<boolean>(() => {
    const selected = this.selectedContributorEmail();
    const current = this.currentUserEmail();

    if (this.isButtonDisabled() || selected === AVERAGE_USER_EMAIL) {
      return true;
    }

    return selected !== undefined && current !== undefined && selected !== current;
  });

  protected readonly hasUnsavedChanges = computed<boolean>(() => {
    return this.criteriaIdsWithUnsavedScores().size > 0;
  });

  protected readonly allCriteriaScoredByAnyone = computed<boolean>(() => {
    return this.comparableCriteria().every((criteria) =>
      criteria.subCriteria.every((subCriteria) =>
        subCriteria.attributes.every((attribute) => attribute.isScoredByAnyone),
      ),
    );
  });

  protected readonly canFinishStep = computed<boolean>(() => {
    return (
      this.isProjectManager() &&
      this.previousPhaseFinished() &&
      !this.isCurrentStepLocked() &&
      this.hasActiveContract() &&
      this.hasCriteria() &&
      this.allCriteriaScoredByAnyone() &&
      !this.hasUnsavedChanges()
    );
  });

  constructor() {
    super();

    effect(() => {
      if (!this.resourcesLoading()) {
        this.initialLoadDone.set(true);
      }
    });
  }

  onContributorSelected(contributorEmail: string): void {
    this.selectedContributorEmail.set(contributorEmail);
  }

  onSearchChanged(term: string): void {
    this.searchTerm.set(term);
  }

  onTabSelected(value: string | number): void {
    this.selectedTabValue.set(value);
    this.openTab(value);

    logTabRender('S', value);
  }

  onConsistencyChanged(change: AttributeConsistencyChange): void {
    this.scoresResource.update((projectScores) => ({
      ...projectScores,
      criteria: projectScores.criteria.map((criteria) => ({
        ...criteria,
        subCriteria: criteria.subCriteria.map((subCriteria) =>
          subCriteria.id === change.subCriteriaId
            ? {
                ...subCriteria,
                attributes: subCriteria.attributes.map((attribute) =>
                  attribute.directusId === change.attributeDirectusId
                    ? {...attribute, consistency: change.consistency}
                    : attribute,
                ),
              }
            : subCriteria,
        ),
      })),
    }));
  }

  onTabSaved(saved: SavedSubCriteriaScores): void {
    this.markClean(saved.evaluationCriteriaId);
    this.applySavedScores(saved);
    void this.refreshStepStatuses();
  }

  private applySavedScores(saved: SavedSubCriteriaScores): void {
    this.scoresResource.update((projectScores) => ({
      ...projectScores,
      criteria: projectScores.criteria.map((criteria) =>
        criteria.id === saved.evaluationCriteriaId
          ? this.withSavedScores(criteria, saved)
          : criteria,
      ),
    }));
  }

  private withSavedScores(
    criteria: EvaluationCriteriaResponse,
    saved: SavedSubCriteriaScores,
  ): EvaluationCriteriaResponse {
    return {
      ...criteria,
      subCriteria: criteria.subCriteria.map((subCriteria) =>
        subCriteria.id === saved.subCriteriaId
          ? {
              ...subCriteria,
              attributes: this.mergeSavedAttributes(subCriteria.attributes, saved.attributes),
            }
          : subCriteria,
      ),
    };
  }

  private mergeSavedAttributes(
    attributes: EvaluationSubCriteriaAttributeView[],
    savedAttributes: EvaluationSubCriteriaAttributeView[],
  ): EvaluationSubCriteriaAttributeView[] {
    const savedByDirectusId = new Map(
      savedAttributes.map((attribute) => [attribute.directusId, attribute]),
    );

    const merged = attributes.map(
      (attribute) => savedByDirectusId.get(attribute.directusId) ?? attribute,
    );
    const added = savedAttributes.filter(
      (attribute) => !attributes.some((item) => item.directusId === attribute.directusId),
    );

    return [...merged, ...added];
  }

  onTabUnsavedScoresChanged(evaluationCriteriaId: number, hasUnsavedScores: boolean): void {
    const criteriaIdsWithUnsavedScores = new Set(this.criteriaIdsWithUnsavedScores());

    if (hasUnsavedScores) {
      criteriaIdsWithUnsavedScores.add(evaluationCriteriaId);
    } else {
      criteriaIdsWithUnsavedScores.delete(evaluationCriteriaId);
    }

    this.criteriaIdsWithUnsavedScores.set(criteriaIdsWithUnsavedScores);
  }

  finishStep(): void {
    this.confirmDialogService.confirm({
      headerKey: 'project.finish.step.6-1.confirm.title',
      messageKey: 'project.finish.step.6-1.confirm.description',
      accept: () => void this.completeStep(),
    });
  }

  private async completeStep(): Promise<void> {
    this.isFinishingStep.set(true);

    try {
      const request: CompleteRfpEvaluationSubcriteriaStepRequestParams = {
        projectId: this.projectId()!,
      };
      await firstValueFrom(
        this.evaluationCriteriaController.completeRfpEvaluationSubcriteriaStep(request),
      );
      await this.refreshStepStatuses();
    } finally {
      this.isFinishingStep.set(false);
    }
  }

  private refreshStepStatuses(): Promise<void> {
    return this.stepState.loadStepStatuses(this.projectId()!);
  }

  private scoresRequest(): {projectId: number; contributorEmail: string} | undefined {
    const projectId = this.projectId();
    const contributorEmail = this.contributorEmail();

    if (projectId === undefined || contributorEmail === undefined) {
      return undefined;
    }

    return {projectId: projectId, contributorEmail: contributorEmail};
  }

  private loadCatalog(): Observable<CriteriaCatalog> {
    return forkJoin({
      criteria: this.directusService.loadEvaluationCriteria(),
      subCriteria: this.directusService.loadSubCriteria(),
      attributes: this.directusService.loadSubCriteriaAttributes(),
    });
  }

  private loadProjectScores(
    projectId: number,
    contributorEmail: string,
  ): Observable<ProjectScores> {
    return this.evaluationCriteriaController
      .getEvaluationCriteriaByProject({
        projectId: projectId,
        averageScores: contributorEmail === AVERAGE_USER_EMAIL,
      })
      .pipe(
        switchMap((criteria) =>
          this.loadSelectedAttributeIds(projectId, criteria).pipe(
            map((attributeIdsBySubCriteria) => ({
              criteria: criteria,
              attributeIdsBySubCriteria: attributeIdsBySubCriteria,
            })),
          ),
        ),
      );
  }

  private loadSelectedAttributeIds(
    projectId: number,
    criteria: EvaluationCriteriaResponse[],
  ): Observable<Map<number, string[]>> {
    const includedSubCriteria = criteria
      .filter((item) => this.isComparable(item))
      .flatMap((item) => item.subCriteria.filter((subCriteria) => subCriteria.included));

    const collectionConfigs = includedSubCriteria.map((subCriteria) => ({
      collectionName: this.attributeCollectionName(subCriteria.id),
      loadFunction: () =>
        from(this.directusService.loadSubCriteriaAttributesBySubCriteria(subCriteria.directusId)),
    }));

    return this.fixedValueService
      .loadOrInitializeFixedValuesBatch(projectId, collectionConfigs)
      .pipe(
        map(
          (idsByCollection) =>
            new Map(
              includedSubCriteria.map((subCriteria) => [
                subCriteria.id,
                idsByCollection[this.attributeCollectionName(subCriteria.id)] ?? [],
              ]),
            ),
        ),
      );
  }

  private isComparable(criteria: EvaluationCriteriaResponse): boolean {
    return (
      criteria.ecNumber > EVALUATION_CRITERIA.EC1 && criteria.ecNumber <= EVALUATION_CRITERIA.EC7
    );
  }

  private attributeCollectionName(subCriteriaId: number): string {
    return `${ATTRIBUTE_COLLECTION_BASE_NAME}_${COLLECTION_POSTFIX}_${subCriteriaId}`;
  }

  private loadPreviousPhaseFinished(projectId: number): Observable<boolean> {
    return this.participatingToolController
      .checkPreviousPhaseFinished({projectId: projectId, phase: TenderPhaseType.RFP})
      .pipe(map((response) => response.isPreviousPhaseFinished));
  }

  private scoreAuthor(): string | undefined {
    const contributorEmail = this.contributorEmail();

    return contributorEmail === AVERAGE_USER_EMAIL ? AVERAGED_SCORE_AUTHOR : contributorEmail;
  }

  private currentUserEmail(): string | undefined {
    return this.projectUserDetail()?.email;
  }

  private toCriteriaTab(
    criteria: EvaluationCriteriaResponse,
    catalog: CriteriaCatalog,
    attributeIdsBySubCriteria: Map<number, string[]>,
    scoreAuthor: string | undefined,
  ): EvaluationCriteriaTabVM {
    const subCriteria = criteria.subCriteria
      .filter((item) => item.included)
      .map((item) =>
        this.toSubCriteriaTab(
          item,
          catalog,
          attributeIdsBySubCriteria.get(item.id) ?? [],
          scoreAuthor,
        ),
      )
      .sort((first, second) => first.scNumber - second.scNumber);

    return {
      id: criteria.id,
      ecNumber: criteria.ecNumber,
      translations: catalog.criteria.find((item) => item.id === criteria.directusId)!.translations,
      subCriteria: subCriteria,
      status: aggregateTabStatus(subCriteria.map((item) => item.status)),
      unresolvedConsistencyCount: subCriteria.reduce(
        (total, item) => total + item.unresolvedConsistencyCount,
        0,
      ),
      clarificationConsistencyCount: subCriteria.reduce(
        (total, item) => total + item.clarificationConsistencyCount,
        0,
      ),
    };
  }

  private toSubCriteriaTab(
    subCriteria: EvaluationSubCriteriaView,
    catalog: CriteriaCatalog,
    selectedAttributeIds: string[],
    scoreAuthor: string | undefined,
  ): SubCriteriaTabVM {
    const attributes = catalog.attributes
      .filter((attribute) => attribute.subcriteria === subCriteria.directusId)
      .filter(
        (attribute) =>
          selectedAttributeIds.length === 0 || selectedAttributeIds.includes(attribute.id),
      )
      .map((attribute) => this.toAttributeScore(attribute, subCriteria.attributes, scoreAuthor))
      .sort((first, second) => first.scaNumber - second.scaNumber);

    return {
      id: subCriteria.id,
      directusId: subCriteria.directusId,
      scNumber: subCriteria.scNumber,
      translations: catalog.subCriteria.find((item) => item.id === subCriteria.directusId)!
        .translations,
      attributes: attributes,
      status: this.progressService.calculateAttributeCompletionStatus(
        attributes.map((attribute) => ({
          id: attribute.id,
          selectedImportanceScore: attribute.score,
        })),
      ),
      unresolvedConsistencyCount: this.consistencyService.countUnresolved(attributes),
      clarificationConsistencyCount: this.consistencyService.countClarificationRequired(attributes),
    };
  }

  private toAttributeScore(
    attribute: SubcriteriaAttributeDirectus,
    savedAttributes: EvaluationSubCriteriaAttributeView[],
    scoreAuthor: string | undefined,
  ): AttributeScoreVM {
    const savedAttribute = savedAttributes.find((item) => item.directusId === attribute.id);

    return {
      id: savedAttribute?.id,
      directusId: attribute.id,
      scaNumber: attribute.sca_number,
      translations: attribute.translations,
      score: this.findScore(savedAttribute?.importanceScores ?? [], scoreAuthor),
      isScoredByAnyone: (savedAttribute?.importanceScores.length ?? 0) > 0,
      consistency: savedAttribute?.consistency,
    };
  }

  private findScore(
    importanceScores: ImportanceScoreView[],
    scoreAuthor: string | undefined,
  ): EvaluationSubCriteriaImportanceScore | undefined {
    if (scoreAuthor === undefined) {
      return undefined;
    }

    return importanceScores.find((score) => score.createdBy === scoreAuthor)?.importanceScore;
  }

  private narrowCriteriaToTerm(
    criteria: EvaluationCriteriaTabVM,
    term: string,
  ): EvaluationCriteriaTabVM | null {
    const subCriteria = criteria.subCriteria
      .map((item) => this.narrowSubCriteriaToTerm(item, term))
      .filter((item) => item !== null);

    if (subCriteria.length > 0) {
      return {...criteria, subCriteria: subCriteria};
    }

    return this.matchesTerm(criteria.translations, term) ? criteria : null;
  }

  private narrowSubCriteriaToTerm(
    subCriteria: SubCriteriaTabVM,
    term: string,
  ): SubCriteriaTabVM | null {
    const attributes = subCriteria.attributes.filter((attribute) =>
      this.matchesAttribute(attribute, term),
    );

    if (attributes.length > 0) {
      return {...subCriteria, attributes: attributes};
    }

    return this.matchesTerm(subCriteria.translations, term) ? subCriteria : null;
  }

  private matchesAttribute(attribute: AttributeScoreVM, term: string): boolean {
    return (
      this.matchesTerm(attribute.translations, term) ||
      `${ATTRIBUTE_IDENTIFIER_PREFIX} ${attribute.scaNumber}`.toLowerCase().includes(term)
    );
  }

  private matchesTerm(translations: DirectusTranslation[] | undefined, term: string): boolean {
    return (translations ?? []).some((translation) =>
      (translation.name ?? '').toLowerCase().includes(term),
    );
  }

  private openTab(value: number | string): void {
    const explicitlyOpenedTabValues = new Set(this.explicitlyOpenedTabValues());
    explicitlyOpenedTabValues.add(value);
    this.explicitlyOpenedTabValues.set(explicitlyOpenedTabValues);
  }

  private markClean(evaluationCriteriaId: number): void {
    const criteriaIdsWithUnsavedScores = new Set(this.criteriaIdsWithUnsavedScores());
    criteriaIdsWithUnsavedScores.delete(evaluationCriteriaId);
    this.criteriaIdsWithUnsavedScores.set(criteriaIdsWithUnsavedScores);
  }
}
